import React from "react";
import { Box, Divider, Skeleton, Stack, TableRow, TableCell, TableHead, Table, TableContainer, Typography, TableBody } from "@mui/material";
import { useForecast } from "../../hooks/useForecast";
import { Product } from "@domain/product/models/Product";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";

interface ForecastParametersProps {
  sourceType: ForecastSourceType;
  source: string;
  connector: string;
  product?: Product;
}

const ForecastParameters: React.FC<ForecastParametersProps> = ({ sourceType, source, connector, product }) => {
  const { forecast, isLoading, error } = useForecast({ sourceType, source, connector });

  const pretty = (obj?: Record<string, any>) => {
    try {
      return JSON.stringify(obj ?? {}, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Stack direction="column" spacing={2} sx={{ flex: 1, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction="column" spacing={2} justifyContent="space-between">
          <Typography variant="subtitle2" gutterBottom>
            Forecast Parameters
          </Typography>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : error ? (
            <Typography color="error">Failed to load parameters</Typography>
          ) : (
            <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {pretty(forecast?.parameters)}
            </Box>
          )}
        </Stack>

        <Divider />

        <Stack direction="column" spacing={2} justifyContent="space-between">

          <Typography variant="subtitle2" gutterBottom>
            Detected Parameters
          </Typography>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : error ? (
            <Typography color="error">Failed to load detected parameters</Typography>
          ) : (
            <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {pretty(forecast?.detectedParameters)}
            </Box>
          )}
        </Stack>

        <Divider />
        

        {product && ( 
          <Stack direction="column" spacing={2} justifyContent="space-between">

          <Typography variant="subtitle2" gutterBottom>
            Product Parameters
          </Typography>

          <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {pretty(product?.forecastParameters)}
          </Box>
        </Stack>
        )}
      </Stack>




      <Box sx={{ flex: 1, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Forecast Analytics
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : error ? (
          <Typography color="error">Failed to load forecast analytics</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Mean Error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.me.toFixed(1)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Mean Percentage Error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.mpe ? (forecast?.forecastAnalytics?.mpe * 100).toFixed(0) + '%' : 'N/A'}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Mean Error Scaled</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.me_scaled ? (forecast?.forecastAnalytics?.me_scaled * 100).toFixed(0) + '%' : 'N/A'}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Root mean squared error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.rmse.toFixed(1)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Mean squared error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.mse.toFixed(1)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Mean absolute error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.mae.toFixed(1)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Mean absolute percentage error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.mape ? (forecast?.forecastAnalytics?.mape * 100).toFixed(0) + '%' : 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Median absolute percentage error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.mdape ? (forecast?.forecastAnalytics?.mdape * 100).toFixed(0) + '%' : 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Symmetric mean absolute percentage error</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.smape ? (forecast?.forecastAnalytics?.smape * 100).toFixed(0) + '%' : 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Coverage</TableCell>
                  <TableCell>{forecast?.forecastAnalytics?.coverage ? (forecast?.forecastAnalytics?.coverage * 100).toFixed(0) + '%' : 'N/A'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack >
  );
};

export default ForecastParameters;
